import { useState } from "react"
import { useNavigate } from "react-router-dom"

import { MANAGEMENT } from "@/constants"
import { IOrderResponse, OrderStatusType } from "@/shared/types"
import { transformOrderId } from "@/shared/utils"

import { Button, Copy, Dropdown, DropdownItem, Flex, OrderStatusLabel } from "@/shared/components"
import { IconChevronDown, IconChevronTop } from "@/shared/icons"

interface IConnectedOrdersDropdownProps {
  connectedOrders: IOrderResponse[]
  currentOrderId: string
  organizationId: string
}

export const ConnectedOrdersDropdown = ({
  connectedOrders,
  currentOrderId,
  organizationId,
}: IConnectedOrdersDropdownProps) => {
  const navigate = useNavigate()
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)

  if (connectedOrders.length <= 1) {
    return null
  }

  const currentIndex = connectedOrders.findIndex((order) => order.orderId === currentOrderId)
  const currentPosition = currentIndex >= 0 ? currentIndex + 1 : connectedOrders.length

  return (
    <Dropdown
      asChild
      open={isDropdownOpen}
      onOpenChange={() => setIsDropdownOpen(!isDropdownOpen)}
      trigger={
        <Button action="secondary" size="small" type="button" ariaLabel="Connected orders">
          <Flex align="center" css={{ gap: "$4" }}>
            <Copy as="span" fontWeight="bold" css={{ whiteSpace: "nowrap" }}>
              {`Connected orders (${currentPosition}/${connectedOrders.length})`}
            </Copy>
            {isDropdownOpen ? <IconChevronTop size="xs" /> : <IconChevronDown size="xs" />}
          </Flex>
        </Button>
      }
    >
      {connectedOrders.map((order, index) => {
        const isCurrent = order.orderId === currentOrderId

        return (
          <DropdownItem
            key={order.orderId}
            label={transformOrderId(order.orderId, organizationId) || order.orderId}
            disabled={isCurrent}
            onSelect={() => {
              if (!isCurrent) {
                navigate(`${MANAGEMENT}/organization/${organizationId}/order/${order.orderId}`)
              }
            }}
          >
            <Flex align="center" css={{ gap: "$8" }}>
              <Copy scale={10} color="theme-n6-n5" css={{ minWidth: "$32" }}>
                {`${index + 1}.`}
              </Copy>
              <OrderStatusLabel status={order.orderStatus as OrderStatusType} scale={10} filled />
            </Flex>
          </DropdownItem>
        )
      })}
    </Dropdown>
  )
}
